import {
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { accountCountries } from '../../../../../core/helpers/country.helper';
import { Gender } from '../../../../../core/profile/gender';
import { MessagingType } from '../../../../../core/profile/messaging-type';
import { AnyProfile } from '../../../../../core/profile/profile';
import { emulateClick } from '../../core/a11y';
import { DrawerService } from '../../drawer/drawer.service';
import { AutoCloseDirective } from '../../shared/directives/auto-close.directive';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../shared/directives/progress-click.directive';
import { ByPassPipe } from '../../shared/pipes/by-pass.pipe';
import { AuthService } from '../../shared/services/auth.service';
import { PostalCodeService } from '../../shared/services/postal-code.service';
import { TelInputComponent } from '../../shared/components/tel-input/tel-input.component';
import { FrenchMobileValidator } from '../../shared/validators/validators';
import { CenterService } from '../../shared/services/center.service';

type ProfileForm = {
  erpId: FormControl<number>;
  centerErpId: FormControl<number>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  address: FormControl<string>;
  additionalAddress: FormControl<string>;
  zip: FormControl<string>;
  city: FormControl<string>;
  country: FormControl<string>;
  phone: FormControl<string>;
  gender: FormControl<Gender>;
  birthDay: FormControl<string>;
  birthMonth: FormControl<string>;
  birthYear: FormControl<string>;
  photoURL: FormControl<string>;
  email: FormControl<boolean | null>;
  sms: FormControl<boolean | null>;
  courrier: FormControl<boolean | null>;
};

@Component({
  selector: 'app-quick-edit-profile',
  templateUrl: 'quick-edit-profile.component.html',
  imports: [
    TelInputComponent,
    AutoCloseDirective,
    ByPassPipe,
    HoverGradientDirective,
    NgxTolgeeModule,
    ProgressClickDirective,
    ReactiveFormsModule,
    NgOptimizedImage,
    NgClass
  ]
})
export class QuickEditProfileComponent {
  route = inject(ActivatedRoute);
  authService = inject(AuthService);
  centerService = inject(CenterService);
  drawerService = inject(DrawerService);
  days = Array(31).fill(undefined).map((_, i) => (i + 1).toLocaleString(undefined, { minimumIntegerDigits: 2 }));
  months = Array(12).fill(undefined).map((_, i) => (i + 1).toLocaleString(undefined, { minimumIntegerDigits: 2 }));
  years = Array(new Date().getFullYear() - 1900 + 1).fill(undefined).map((_, i) => String(i + 1900));
  private postalCodes = inject(PostalCodeService);
  cities = signal<string[] | null>(null);
  simplified = input(false);
  form = computed(() => {
    const user = this.authService.user();
    const isSimplified = this.simplified();
    const shouldUpdateConsent = user?.shouldUpdateConsent ?? true;
    const notification = user?.notification ?? [];
    const [ year, month, day ] = (user?.birthDate ?? '1970-01-01').split('-');
    const centerValidators = !isSimplified || user?.shouldUpdateCenter
      ? [ Validators.required, Validators.min(1) ]
      : [];
    const form = new FormGroup<ProfileForm>({
      erpId: new FormControl(user?.erpId ?? -1, { nonNullable: true }),
      centerErpId: new FormControl(
        user?.shouldUpdateCenter ? -1 : (user?.centerErpId ?? this.centerService.center()?.erpId ?? -1),
        { nonNullable: true, validators: centerValidators }),
      firstName: new FormControl(user?.firstName ?? '',
        { nonNullable: true, validators: isSimplified ? [] : [ Validators.required ] }),
      lastName: new FormControl(user?.lastName ?? '',
        { nonNullable: true, validators: isSimplified ? [] : [ Validators.required ] }),
      address: new FormControl(user?.address ?? '',
        { nonNullable: true, validators: isSimplified ? [] : [ Validators.required ] }),
      additionalAddress: new FormControl(user?.additionalAddress ?? '', { nonNullable: true }),
      zip: new FormControl(user?.zip ?? '', {
        nonNullable: true,
        validators: isSimplified ? [] : [ Validators.required, this.postalCodes.zipValidator ]
      }),
      city: new FormControl(user?.city ?? '',
        { nonNullable: true, validators: isSimplified ? [] : [ Validators.required ] }),
      country: new FormControl(user?.country ?? '',
        { nonNullable: true, validators: isSimplified ? [] : [ Validators.required ] }),
      phone: new FormControl(user?.phone ?? '', {
        nonNullable: true,
        validators: isSimplified ? [] : [ Validators.required, FrenchMobileValidator ]
      }),
      gender: new FormControl(user?.gender ?? Gender.Male, { nonNullable: true }),
      birthDay: new FormControl(day, { nonNullable: true }),
      birthMonth: new FormControl(month, { nonNullable: true }),
      birthYear: new FormControl(year, { nonNullable: true }),
      photoURL: new FormControl(user?.photoURL ?? '', { nonNullable: true }),
      email: new FormControl(shouldUpdateConsent ? null : notification.includes(MessagingType.Email), {
        nonNullable: true,
        validators: [ Validators.required ]
      }),
      sms: new FormControl(shouldUpdateConsent ? null : notification.includes(MessagingType.SMS), {
        nonNullable: true,
        validators: [ Validators.required ]
      }),
      courrier: new FormControl(shouldUpdateConsent ? null : notification.includes(MessagingType.Courrier), {
        nonNullable: true,
        validators: [ Validators.required ]
      }),
    });
    if (!isSimplified) {
      this.postalCodes.bind(form, this.cities, true, () => form.disabled);
    }
    return form;
  });
  progress = signal(false);
  activeCenters = computed(() => {
    return this.centerService.centers().filter(c => c.enabled && c.erpId > 0);
  });
  displayCities = computed(() => {
    const cities = this.cities();
    const current = this.form().get('city')?.value;
    return cities?.every(c => c !== current) ?? false;
  });
  protected readonly emulateClick = emulateClick;
  protected readonly Gender = Gender;
  protected readonly sortedCountries = (): ReturnType<typeof accountCountries> => accountCountries(this.authService.user()?.country);

  constructor() {
    effect(() => {
      const progress = this.progress();
      if (progress) {
        this.form().get('phone')?.disable();
      } else {
        this.form().get('phone')?.enable();
      }
    });
  }

  hasCityList(): boolean {
    return this.postalCodes.hasCityList(this.form().get('country')?.value ?? '');
  }

  setCity(city: string): void {
    this.cities.set(null);
    this.form().get('city')?.setValue(city);
  }

  isValidDate(year: string, month: string, day: string): boolean {
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return date.getTime() <= Date.now();
  }

  /**
   * Call update profile API with updated user data
   */
  updateProfile = async (): Promise<void> => {
    const form = this.form();
    const isSimplified = this.simplified();
    form.disable();
    const profileData = form.getRawValue();
    const user = this.authService.user();
    if (user) {
      this.progress.set(true);
      const notification: MessagingType[] = [];
      if (profileData.email) notification.push(MessagingType.Email);
      if (profileData.sms) notification.push(MessagingType.SMS);
      if (profileData.courrier) notification.push(MessagingType.Courrier);

      const profile: AnyProfile = isSimplified
        ? {
            simplified: true,
            centerErpId: profileData.centerErpId,
            notification,
            shouldUpdateCenter: user.shouldUpdateCenter,
            shouldUpdateConsent: false,
          }
        : {
            erpId: profileData.erpId,
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            photoURL: profileData.photoURL,
            phone: profileData.phone,
            address: profileData.address,
            additionalAddress: profileData.additionalAddress,
            zip: profileData.zip,
            city: profileData.city,
            country: profileData.country,
            birthDate: `${profileData.birthYear}-${profileData.birthMonth}-${profileData.birthDay}`,
            gender: profileData.gender,
            centerErpId: profileData.centerErpId,
            shouldUpdateCenter: user.shouldUpdateCenter,
            shouldUpdateConsent: false,
            notification,
          };

      try {
        await this.authService.updateProfile(profile);
        await this.authService.getIdToken(true);
        this.drawerService.close();
      } finally {
        this.progress.set(false);
        form.enable();
      }
    }
  }
}
